// src/routes/(auth)/oauth-callback.tsx
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { z } from 'zod'
import { supabase } from '@/lib/supabaseClient'
import { exchangeYoutubeCode } from '@/lib/youtube-auth'
import { useAuth } from '@/features/auth/auth-context'

// Updated validation to include state parameter
export const Route = createFileRoute('/(auth)/oauth-callback')({
  validateSearch: z.object({
    code: z.string().optional(),
    error: z.string().optional(),
    error_description: z.string().optional(),
    state: z.string().optional(),
  }),
  component: OAuthCallback,
})

type ProcessingStatus = 'loading' | 'processing' | 'success' | 'error'

function OAuthCallback() {
  const search = useSearch({ from: '/(auth)/oauth-callback' })
  const navigate = useNavigate()
  const hasExchangedRef = useRef(false)
  const processedCodeRef = useRef<string | null>(null)
  const { loading } = useAuth()
  
  const [status, setStatus] = useState<ProcessingStatus>('loading')
  const [errorMessage, setErrorMessage] = useState<string>('')

  useEffect(() => {
    const exchangeIfNeeded = async () => {
      console.log('[OAuthCallback] 🔍 Starting OAuth callback process')
      console.log('[OAuthCallback] 🔍 Search params:', search)
      console.log('[OAuthCallback] 🔍 Current URL:', window.location.href)
      
      // Clear URL immediately to prevent code reuse
      if (search.code || search.error) {
        const newUrl = window.location.pathname
        window.history.replaceState({}, document.title, newUrl)
        console.log('[OAuthCallback] 🔄 URL cleared to prevent code reuse')
      }
      
      // Prevent multiple executions with the same code
      if (hasExchangedRef.current || (search.code && processedCodeRef.current === search.code)) {
        console.log('[OAuthCallback] ⚠️ Already processed this code, skipping')
        return
      }
      hasExchangedRef.current = true
      if (search.code) processedCodeRef.current = search.code

      try {
        setStatus('processing')

        // Handle OAuth errors from Google first
        if (search.error) {
          const errorMsg = search.error_description || search.error || 'OAuth authorization failed'
          console.error('[OAuthCallback] ❌ OAuth error from Google:', search.error, search.error_description)
          throw new Error(errorMsg)
        }

        // Check for authorization code
        if (!search.code) {
          console.error('[OAuthCallback] ❌ No authorization code received')
          throw new Error('No authorization code received from Google')
        }

        // Decode and log state information
        if (search.state) {
          try {
            const stateData = JSON.parse(Buffer.from(search.state, 'base64').toString())
            console.log('[OAuthCallback] 🔍 Decoded state:', stateData)
          } catch (e) {
            console.warn('[OAuthCallback] ⚠️ Could not decode state parameter')
          }
        }

        console.log('[OAuthCallback] 🔄 Getting Supabase user...')
        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser()

        if (userError) {
          console.error('[OAuthCallback] ❌ Supabase user error:', userError)
          throw new Error(`Authentication error: ${userError.message}`)
        }

        if (!user) {
          console.warn('[OAuthCallback] ❌ No Supabase user found')
          navigate({ to: '/sign-in', search: { message: 'Please sign in first' } })
          return
        }

        console.log('[OAuthCallback] 🔄 Getting Supabase session...')
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession()

        if (sessionError) {
          console.error('[OAuthCallback] ❌ Session error:', sessionError)
          throw new Error(`Session error: ${sessionError.message}`)
        }

        if (!session?.access_token) {
          console.error('[OAuthCallback] ❌ No valid session found')
          throw new Error('No valid session found')
        }

        console.log('[OAuthCallback] 🔄 Exchanging YouTube authorization code...')
        console.log('[OAuthCallback] 🔍 Code length:', search.code.length)
        
        // Pass the state parameter to the exchange function
        const exchangePromise = exchangeYoutubeCode(search.code, session.access_token, search.state)
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('OAuth exchange timed out after 30 seconds')), 30000)
        )

        await Promise.race([exchangePromise, timeoutPromise])
        
        console.log('[OAuthCallback] ✅ YouTube OAuth exchange successful')
        setStatus('success')
        
        // Brief delay to show success state
        setTimeout(() => {
          navigate({ to: '/', search: { success: 'youtube-connected' } })
        }, 1500)

      } catch (err: any) {
        console.error('[OAuthCallback] ❌ Exchange failed:', err)
        setStatus('error')
        
        // Better error message handling
        let message = 'Failed to connect YouTube account'
        if (err.message?.includes('invalid_grant')) {
          message = 'Authorization code expired or already used. Please try again.'
        } else if (err.message?.includes('timeout')) {
          message = 'Connection timed out. Please check your internet and try again.'
        } else if (err.message?.includes('CODE_REUSE')) {
          message = 'This authorization code was already used. Please restart the OAuth flow.'
        } else if (err.message?.includes('INVALID_GRANT')) {
          message = 'Authorization code is invalid or expired. Please try connecting again.'
        } else if (err.message) {
          message = err.message
        }
        
        setErrorMessage(message)
        
        // Auto-redirect after showing error
        setTimeout(() => {
          navigate({ to: '/', search: { error: 'oauth-failed' } })
        }, 5000)
      }
    }

    // Only run when not loading and we have search params
    if (!loading && (search.code || search.error)) {
      exchangeIfNeeded()
    }
  }, [loading]) // Keep dependencies minimal

  // UI components remain the same
  if (status === 'loading' || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h1 className="text-lg font-semibold">Loading...</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Initializing OAuth callback...
          </p>
        </div>
      </div>
    )
  }

  if (status === 'processing') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h1 className="text-xl font-semibold mb-2">Connecting YouTube Account...</h1>
          <p className="text-muted-foreground">
            Please wait while we securely link your YouTube account.
          </p>
          <div className="mt-4 text-sm text-muted-foreground">
            This usually takes a few seconds...
          </div>
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs text-blue-600">
              🔒 We're exchanging your authorization code for secure tokens
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h1 className="text-xl font-semibold text-red-600 mb-2">Connection Failed</h1>
          <p className="text-muted-foreground mb-4">{errorMessage}</p>
          <div className="space-y-3">
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-700">
                💡 <strong>Common solutions:</strong>
              </p>
              <ul className="text-xs text-red-600 mt-2 space-y-1 text-left">
                <li>• Try the OAuth flow again</li>
                <li>• Check your internet connection</li>
                <li>• Make sure you're signed in to your account</li>
              </ul>
            </div>
            <button
              onClick={() => navigate({ to: '/' })}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
            >
              Return to Dashboard
            </button>
            <p className="text-sm text-muted-foreground">
              Redirecting automatically in a few seconds...
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-green-500 text-5xl mb-4">✅</div>
          <h1 className="text-xl font-semibold text-green-600 mb-2">YouTube Connected!</h1>
          <p className="text-muted-foreground mb-4">
            Your YouTube account has been successfully linked.
          </p>
          <div className="p-4 bg-green-50 rounded-lg border border-green-200 mb-4">
            <p className="text-sm text-green-700">
              🎉 You can now upload videos directly to YouTube!
            </p>
          </div>
          <div className="animate-pulse text-sm text-muted-foreground">
            Redirecting you back to your dashboard...
          </div>
        </div>
      </div>
    )
  }

  return null
}